import React, { useState } from 'react';
import { Typography, Space, Tag, Image, Divider, Button } from 'antd';
import { BlindTypeMap, MergedOrder, OrderStatusMap } from '../../../types/process/order';
import { ORDER_STATUS_LABEL_MAP, type OrderTags, SendTypeMap, ReturnTypeMap, LogisticsNameMap } from '../../../types/process/order-data';
import { useShopMap } from '../../../services/store/organization.store';
import { InfoCircleOutlined, ExclamationCircleFilled, ClockCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { formatRiskQualitative } from './effect';
import { AccessoryModal } from './modals/AccessoryModal';
import { BuyoutInfoModal } from './modals/BuyoutInfoModal';
import { OperationLogModal } from './modals/OperationLogModal';
import { LogisticsTrackModal } from './modals/LogisticsTrackModal';
import OrderPreRatingSummary from './OrderPreRatingSummary';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;

interface OrderBlockShowProps {
  order: MergedOrder;
  renderActions?: (order: MergedOrder) => React.ReactNode;
  headerExtra?: React.ReactNode;
}

const OrderBlockShowSimple: React.FC<OrderBlockShowProps> = ({ order, renderActions, headerExtra }) => {
  const { order: orderEntity, orderData, orderRecord } = order;
  const { shopMap } = useShopMap();
  const [isAccessoryModalVisible, setIsAccessoryModalVisible] = useState(false);
  const [isBuyoutModalVisible, setIsBuyoutModalVisible] = useState(false);
  const [isLogModalVisible, setIsLogModalVisible] = useState(false);
  const [isTrackModalVisible, setIsTrackModalVisible] = useState(false);

  const isApplyingCancel = orderRecord?.cmptStat?.apply_order === 1;
  const cancelData = orderRecord?.cancelData;

  // Compact styles
  const mainPadding = '8px 12px';
  const imageSize = 60;
  const fontSize = 12;
  const dividerMargin = '4px 0';

  const safeGet = (val: unknown, suffix = '') => (val === undefined || val === null || val === '' ? '-' : `${val}${suffix}`);
  const formatMoney = (val: unknown) => (val === undefined || val === null || val === '' ? '-' : `¥${Number(val).toFixed(2)}`);

  const formatAddress = () => {
    if (orderData?.userAddress) return orderData.userAddress;
    const parts = [orderData?.province, orderData?.city, orderData?.district].filter((p) => p);
    return parts.length > 0 ? parts.join('') : '-';
  };

  const shopName = shopMap.get(orderEntity.storeId)?.name || orderEntity.storeId;
  const tags: Partial<OrderTags> = orderData?.tags ?? {
    zftOrder: orderData?.zftOrder,
    payType: orderData?.payType,
    cancelControl: orderData?.cancelControl,
  };

  return (
    <div style={{
      border: '1px solid #f0f0f0',
      marginBottom: 12,
      background: '#fff',
      fontSize
    }}>
      {/* Top Bar: Compact Order Info */}
      <div style={{
        background: '#f5f7fa',
        padding: '8px 12px',
        borderBottom: '1px solid #f0f0f0',
      }}>
        {/* Row 1 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space size={16}>
            <Text type="secondary" style={{ fontSize }}>{new Date(orderEntity.orderCreatedAt).toLocaleString()}</Text>
            <Text type="secondary" style={{ fontSize }}>订单号: <Text strong style={{ color: '#000', fontSize }}>{orderEntity.orderNo}</Text></Text>
            <Text type="secondary" style={{ fontSize }}>店铺: {shopName}</Text>
            <Text type="secondary" style={{ fontSize }}>
              寄出单号: {orderData?.logisticsInfo?.logisticNumber ? (
                <>
                  <Text strong style={{ color: '#1890ff', fontSize, cursor: 'pointer' }} onClick={() => setIsTrackModalVisible(true)}>
                    {orderData.logisticsInfo.logisticNumber}
                  </Text>
                  {orderData.logisticsInfo.logisticName && (
                    <Text style={{ marginLeft: 4 }}>
                      【{LogisticsNameMap[orderData.logisticsInfo.logisticName] || orderData.logisticsInfo.logisticName}】
                    </Text>
                  )}
                </>
              ) : '-'}
            </Text>
          </Space>
          <OrderPreRatingSummary orderNo={orderEntity.orderNo} preRating={order.preRating} headerExtra={headerExtra} />
        </div>
        {/* Row 2: Tags */}
        <div style={{ marginTop: 4 }}>
          <Space size={4}>
            {tags?.zftOrder && <Tag color="red" style={{ margin: 0 }}>直付通</Tag>}
            {tags?.payType &&
              (tags.payType === '支付宝免密代扣' ? <Tag color="red" style={{ margin: 0 }}>{tags.payType}</Tag> : <Tag color="blue" style={{ margin: 0 }}>{tags.payType}</Tag>)}
            {tags?.renewalOrder && <Tag color="blue" style={{ margin: 0 }}>续租</Tag>}
            {orderData?.supportRenewal && <Tag color="green" style={{ margin: 0 }}>支持续租</Tag>}
            {orderData?.appType && <Tag color="blue" style={{ margin: 0 }}>{orderData.appType}</Tag>}
            {orderEntity.blindType && <Tag color="orange" style={{ margin: 0 }}>{BlindTypeMap[orderEntity.blindType]}</Tag>}
          </Space>
        </div>
      </div>

      {/* Main Content: Flex Layout */}
      <div style={{ display: 'flex' }}>
        {/* Left Section (Cols 1-5 + Bottom Row) */}
        <div style={{ flex: 8.5, display: 'flex', flexDirection: 'column', borderRight: '1px solid #f0f0f0' }}>
          {/* Top Row: 5 Columns */}
          <div style={{ display: 'flex', borderBottom: '1px solid #f0f0f0' }}>
            {/* Col 1: Product */}
            <div style={{ flex: 2.5, padding: mainPadding, borderRight: '1px solid #f0f0f0', display: 'flex', gap: 8 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <Image
                  width={imageSize}
                  height={imageSize}
                  src={orderData?.spuCoverUrl || "error"}
                  style={{ borderRadius: 4, objectFit: 'cover' }}
                />
                {((orderData?.accessoryData?.length || 0) > 0) && (
                  <Button type="link" size="small" onClick={() => setIsAccessoryModalVisible(true)} style={{ padding: 0, height: 'auto', fontSize }}>
                    配件清单({orderData?.accessoryData?.length})
                  </Button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Text strong style={{ fontSize: 13, lineHeight: 1.2 }}>{orderData?.productTitle || '未知商品'}</Text>
                  {orderData?.productTitle && <Paragraph copyable={{ text: orderData.productTitle }} style={{ margin: 0, color: '#1890ff' }} />}
                </div>
                <Text type="secondary" style={{ fontSize }}>套餐: {orderData?.skuName || '-'}</Text>
                <Text type="secondary" style={{ fontSize }}>
                  租期: {orderData?.tenancyData?.start_time ? new Date(orderData.tenancyData.start_time).toLocaleDateString() : '-'} ~
                  {orderData?.tenancyData?.end_time ? new Date(orderData.tenancyData.end_time).toLocaleDateString() : '-'}
                  {orderData?.tenancyData?.total_days ? ` (共${orderData.tenancyData.total_days}天)` : ''}
                </Text>
              </div>
            </div>

            {/* Col 2: Money 1 */}
            <div style={{ flex: 2, padding: mainPadding, borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">押金:</Text>
                <Text>{formatMoney(orderData?.depositValue)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">信用评估额度:</Text>
                <Text>{orderData?.creditEvaluatieQuota ? `¥${Number(orderData.creditEvaluatieQuota).toFixed(2)}` : '-'}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">商品押金:</Text>
                <Text>{formatMoney(orderData?.productValue)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">已冻结押金:</Text>
                <Text style={{ color: '#ff4d4f' }}>{formatMoney(orderData?.alreadyFrozen)}</Text>
              </div>
              <Divider style={{ margin: dividerMargin }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">首期:</Text>
                <Text>{formatMoney(orderData?.firstRent || orderData?.installmentPrice)}</Text>
              </div>
            </div>

            {/* Col 3: Money 2 */}
            <div style={{ flex: 2, padding: mainPadding, borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">实付款:</Text>
                <Text strong style={{ color: '#ff4d4f' }}>{formatMoney(orderData?.hadPayRental || orderData?.receivedRental)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">付款时间:</Text>
                <Text style={{ color: '#ff4d4f', fontSize: 10 }}>{orderData?.paySuccessDate ? dayjs(orderData.paySuccessDate).format('YYYY-MM-DD HH:mm:ss') : '-'}</Text>
              </div>
              <Divider style={{ margin: dividerMargin }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">租金:</Text>
                <Text strong>{formatMoney(orderData?.hadPayRental || orderData?.receivedRental)} /{formatMoney(orderData?.receivableRental || orderData?.rentalNeedPay || orderData?.originRent)}</Text>
              </div>
            </div>

            {/* Col 4: User */}
            <div style={{ flex: 2, padding: mainPadding, borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Text strong>{orderEntity.userName} {orderEntity.userPhone}</Text>
                <Paragraph copyable={{ text: `${orderEntity.userName} ${orderEntity.userPhone}` }} style={{ margin: 0, color: '#1890ff' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'start', gap: 4 }}>
                <Text type="secondary" ellipsis={{ tooltip: formatAddress() }} style={{ maxWidth: 180 }}>
                  {formatAddress()}
                </Text>
                <Paragraph copyable={{ text: formatAddress() }} style={{ margin: 0, color: '#1890ff' }} />
              </div>
              <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Text type="secondary">注册手机: {safeGet(orderData?.userRegisterPhone)}</Text>
                {orderData?.userRegisterPhone && <Paragraph copyable={{ text: orderData.userRegisterPhone }} style={{ margin: 0, color: '#1890ff' }} />}
              </div>
            </div>

            {/* Col 5: Stats */}
            <div style={{ flex: 2, padding: mainPadding, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text style={{ color: '#ff4d4f', textDecoration: 'underline', cursor: 'pointer', fontSize: 11 }}>公司: 成功{orderData?.orderStatCount?.companySuccessOrderCount || 0}次</Text>
                <Text style={{ color: '#ff4d4f', textDecoration: 'underline', cursor: 'pointer', fontSize: 11 }}>累计{orderData?.orderStatCount?.companyOrderCount || 0}次</Text>
              </div>
              {order.orderRecord?.orderStatistic && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text style={{ textDecoration: 'underline', cursor: 'pointer', fontSize: 11 }}>平台: 成功{order.orderRecord.orderStatistic.success_order}次</Text>
                  <Text style={{ textDecoration: 'underline', cursor: 'pointer', fontSize: 11 }}>累计{order.orderRecord.orderStatistic.total_order}次</Text>
                </div>
              )}
              <Divider style={{ margin: dividerMargin }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">数量:</Text>
                <Text>{orderData?.quantity}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary" style={{ display: 'flex', alignItems: 'center' }}>
                  到期购买价:
                  {order.orderRecord?.buyoutInfo?.buyout_list && order.orderRecord.buyoutInfo.buyout_list.length > 0 && (
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: '#1890ff', cursor: 'pointer' }}
                      onClick={() => setIsBuyoutModalVisible(true)}
                    />
                  )}
                </Text>
                <Text style={{ color: '#1890ff' }}>{formatMoney(orderData?.buyoutPrice)}</Text>
              </div>
            </div>
          </div>

          {/* Bottom Row: Auth | Risk (Two Rows) */}
          <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 8, background: '#fff' }}>
            <Space split={<Divider type="vertical" />}>
              <Text type="secondary">认证资料: {safeGet(orderData?.userAuthName)}</Text>
              <Text type="secondary">{safeGet(orderData?.userAuthIdCard)}</Text>
              <Text type="secondary">{orderData?.faceRecognitionStatus}</Text>
              <Text type="secondary">性别: {safeGet(orderData?.userSex)}</Text>
              <Text type="secondary">年龄: {safeGet(orderData?.userAge)}</Text>
            </Space>

            {/* Risk Info */}
            {(() => {
              const qualitative = String(orderData?.riskQualitative ?? '').trim();
              const result = String(orderData?.riskResult ?? '').trim();
              const content = String(orderData?.riskContent ?? '').trim();
              const formatted = formatRiskQualitative(qualitative);

              if (qualitative) {
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag color={formatted.tagColor as any} style={{ borderRadius: 12, padding: '0 10px', marginInlineEnd: 0 }}>
                      {formatted.tagText}
                    </Tag>
                    {result ? <Text style={{ color: formatted.textColor }}>{result}</Text> : null}
                  </div>
                );
              }
              if (content) {
                return <Text style={{ color: '#faad14' }}>{content}</Text>;
              }
              if (!qualitative && !content) {
                return <Text style={{ color: '#faad14' }}>【未风控】 尚未抓取到风控报告</Text>;
              }
              return null;
            })()}
          </div>
        </div>

        {/* Right Section: Status & Actions (Flex 1.5) */}
        <div style={{ flex: 1.5, padding: mainPadding, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', width: '100%' }}>
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#faad14', marginRight: 6 }}>●</span>
              <Text style={{ color: '#000' }}>人人租订单状态: {ORDER_STATUS_LABEL_MAP[orderData?.orderStatus || 0]}</Text>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#faad14', marginRight: 6 }}>●</span>
              <Text style={{ color: '#000' }}>系统工单状态: {OrderStatusMap[orderEntity.orderStatus]}</Text>
            </div>

            {/* Condition: Apply Cancel (Red Box) */}
            {isApplyingCancel && cancelData && (
              <div style={{
                background: '#fff1f0',
                border: '1px solid #ffccc7',
                borderRadius: 4,
                padding: 8,
                marginTop: 8,
                textAlign: 'left'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                  <ExclamationCircleFilled style={{ color: '#ff4d4f', marginRight: 8 }} />
                  <Text style={{ color: '#000' }}>申请取消原因: {cancelData.cancel_reason}</Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                  <Text style={{ color: '#52c41a' }}>拦截成功: {cancelData.auto_cancel_order_count_down_text || '即将自动取消'}</Text>
                </div>
              </div>
            )}
          </div>



          {/* Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', alignItems: 'center' }}>
            {renderActions && renderActions(order)}
          </div>
        </div>
      </div>

      {/* Operation Logs (F) */}
      {(order.orderRecord?.operationLog && order.orderRecord.operationLog.length > 0) && (
        <div
          style={{ padding: '8px 12px', borderTop: '1px solid #f0f0f0', backgroundColor: '#fafafa', fontSize: 12, color: '#666', cursor: 'pointer' }}
          onClick={() => setIsLogModalVisible(true)}
        >
          {(() => {
            const logs = order.orderRecord!.operationLog!;
            const lastLog = logs[0];
            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>{lastLog.remark} - {lastLog.remark_by} - {lastLog.created_at} / {logs.length}次记录</span>
              </div>
            );
          })()}
        </div>
      )}

      <AccessoryModal
        visible={isAccessoryModalVisible}
        onClose={() => setIsAccessoryModalVisible(false)}
        accessoryData={orderData?.accessoryData}
      />
      <BuyoutInfoModal
        visible={isBuyoutModalVisible}
        onClose={() => setIsBuyoutModalVisible(false)}
        buyoutInfo={order.orderRecord?.buyoutInfo}
      />
      <OperationLogModal
        visible={isLogModalVisible}
        onClose={() => setIsLogModalVisible(false)}
        logs={order.orderRecord?.operationLog}
      />
      <LogisticsTrackModal
        visible={isTrackModalVisible}
        onClose={() => setIsTrackModalVisible(false)}
        logisticsNo={orderData?.logisticsInfo?.logisticNumber}
      />
    </div>
  );
};

export default OrderBlockShowSimple;
